import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { PageHero, CTASection, Stat } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { IMG, ACHIEVEMENTS, OUTCOME_VIDEOS, RECOGNITIONS, STUDENT_POSTERS } from "@/data/content";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { Trophy, GraduationCap, Globe2, Award, Sparkles, PlayCircle, Quote, CheckCircle2, Star } from "lucide-react";

const CHART = [
  { name: "Cambridge A/L", value: 100 },
  { name: "AMC", value: 80 },
  { name: "Olympiads", value: 92 },
  { name: "UK / US 100", value: 100 },
  { name: "Board", value: 100 },
];

const TESTIMONIALS = [
  { q: "The long verses of Sanskrit and Arabic drama were free-flowing from the kids — I was simply speechless. The performances are amazing. Sir, your instructions to parents are so impactful; I really admire your concern for the students.", a: "Dr. Sheetal Modi", r: "Parent · The Creator International School" },
  { q: "Gayatri joined in Grade 2 in 2018-19 and her personality has developed in every sense — she expresses her thoughts very clearly, her thinking has grown, and she is eager to find answers to questions that arise around her. Within a year she grasped Sanskrit beautifully and topped the marks. The system and culture of this school is unique — I haven't seen anything like it even in metro cities like Pune. Sir is very co-operative, and the principal and teachers do their job from the heart.", a: "Prof. M. C. Hingonekar", r: "Parent · Faculty, Govt. Engg. College, Pune" },
];

const EDUCATOR_FEEDBACK = [
  {
    a: "Krantish Sir",
    r: "Educator · Add Math, A Level Math",
    img: null,
    q: "I conducted a PBL-based experiment on the displacement of a cart with respect to time. Students observed that the displacement–time graph obtained was quadratic in nature, which made the learning process highly interactive and concept-oriented. This single experiment helped me effectively cover multiple sub-concepts of the chapter — modelling of a quadratic equation, its formation from observations, nature of roots, the vertex of a quadratic graph, the relationship between roots and vertex, and the physical significance of the graph in relation to the cart's position. Students were actively involved in observation, data collection, graph plotting and interpretation. This PBL approach created a positive learning environment and enhanced students' conceptual clarity.",
  },
  {
    a: "Mathematics Facilitator",
    r: "Grade 4 · Daily classroom feedback",
    img: null,
    q: "I demonstrated what happens when decimals are multiplied by 10 (3.45 × 10 = 34.5). Learners understood it in 15 minutes! Seriously, I checked the time — in 15 minutes they understood how the decimal point moves after being multiplied by 10. Last year, you won't believe, I took the entire week to explain this.",
  },
  {
    a: "Physics Facilitator",
    r: "Sensor-based STEM activity",
    img: null,
    q: "Today I conducted a hands-on activity using a light-gate sensor to measure the speed of a model car. As the car passed through the gate, the sensor recorded the exact time the beam was interrupted — letting us calculate speed accurately. What impressed me most was how consistent and precise the recorded data was: with multiple trials at different speeds we obtained clear velocity–time graphs, analysed patterns and discussed average vs instantaneous speed. Students were engaged, curious, and eager to understand how mathematical formulas translate into real-world measurements. Mathematics becomes meaningful when learners connect theory with experiments and data. Overall, this sensor-based activity added tremendous value to our teaching process and opened up opportunities for further STEM-based explorations.",
  },
  {
    a: "AS Mathematics Facilitator",
    r: "PBL Activity · Completing the Square",
    img: "/edu-fb-tiles.jpg",
    q: "AS Math completed their first PBL activity on completing the square. Students explored the method through a hands-on activity using algebraic tiles — actively forming perfect square shapes, identifying missing parts, and visually understanding how a quadratic expression can be rewritten. The activity developed conceptual clarity, improved engagement and encouraged collaborative learning, connecting algebraic expressions with geometric representation.",
  },
  {
    a: "Mathematics Facilitator",
    r: "Hands-on lesson · powered by the CoPE-App",
    img: "/edu-fb-rounding.jpg",
    q: "Rounding off to significant figures is often seen as abstract, and in previous years students took longer to grasp it despite regular practice. This time, we incorporated structured, hands-on activities where students actively engaged with the concept — and using the CoPE app added clarity and direction to the lesson. Students even connected the concept to real life: rounding off prices while shopping, estimating distances and simplifying measurements in science. They were not just learning the procedure, but actually understanding its practical use.",
  },
  {
    a: "Mathematics Facilitator",
    r: "Number-line activity · guided by the CoPE-App",
    img: "/edu-fb-numberline.jpg",
    q: "Negative numbers on the number line — last year I took 4 lectures to clear this concept, but today I did it in 2. I made a number line last year also, so what is different this year? This year kids were using counters (plastic tokens) to jump from one number to another instead of just tracing jumps in air. Just a small difference, mentioned in the CoPE App, created a huge difference. So yes, we were doing activities last year also — but this year it is more structured, more scientific, and made in such a way that all kids are understanding.",
  },
];

const FEATURED_TESTIMONIALS = [
  {
    name: "Mukta Joshi",
    role: "Student · Part of TCIS Community for 9 Years",
    paras: [
      "I am Mukta Joshi, and I have been a proud member of THE CREATOR INTERNATIONAL SCHOOL, KHAMGAON for the last nine years. Over these years, I have received one of the best educational experiences in Khamgaon. It is truly remarkable that students in a small town have access to the quality of education offered by the Cambridge curriculum. Our management, teachers, and administrative team continuously work with dedication to ensure every student's growth and success.",
      "One of the most unique aspects of our school is the PBL-TDLE (Problem-Based Learning through Teaching by Demonstrations and Learning by Experiments) framework. Instead of simply learning concepts through textbooks, we understand them through demonstrations, experiments, investigations, and problem solving. This has helped me develop confidence, analytical thinking, and a much deeper understanding of science and mathematics.",
      "Our school has produced outstanding achievements, including International Rank 1 in the International General Knowledge Olympiad (IGKO), making us proud to be part of such an institution. I also appreciate the opportunity to learn Sanskrit and Arabic as second languages, which has helped us understand our culture and heritage while receiving a truly international education.",
      "I feel fortunate to be a student of THE CREATOR INTERNATIONAL SCHOOL and would strongly recommend it to every parent looking for quality, research-based education.",
    ],
  },
  {
    name: "Anand Sharma",
    role: "Parent",
    paras: [
      "I am extremely happy with THE CREATOR INTERNATIONAL SCHOOL. The teachers have helped my daughter learn many new things and have played a significant role in improving her confidence, academic performance, and overall personality. Every teacher and member of the support staff works sincerely for the benefit of the students.",
      "Initially, we were concerned about online learning, but the school made the transition smooth and engaging. The teachers ensured that students remained connected, motivated, and continued learning effectively from home by providing excellent study materials and continuous guidance.",
      "What impresses me most is the school's PBL-TDLE learning framework, where students do not simply memorize concepts but actually develop them through demonstrations, experiments, investigations, and advanced problem-solving activities. This approach has greatly improved my daughter's understanding, confidence, and ability to apply concepts in real-life situations.",
      "The school has truly brought world-class education to a small town, and I sincerely appreciate the continuous efforts of the entire team in making learning enjoyable, meaningful, and effective. Thank you for providing such high-quality education to our children.",
    ],
  },
  {
    name: "Mangala Mahajan",
    role: "Teacher (M.A. English, M.Ed.) · 16+ Years of Teaching Experience",
    paras: [
      "Having more than 16 years of teaching experience across Gujarat, Bihar, and Maharashtra in both State Board and CBSE schools, I can confidently say that THE CREATOR INTERNATIONAL SCHOOL, KHAMGAON offers one of the most innovative educational environments I have experienced.",
      "The school's greatest strength is its PBL-TDLE (Problem-Based Learning through Teaching by Demonstrations and Learning by Experiments) framework. It transforms traditional classroom teaching into an engaging learning process where every concept is developed through experimentation, investigation, and systematic problem solving. This not only strengthens conceptual understanding but also develops scientific thinking, creativity, confidence, and independent learning among students.",
      "As a teacher, I find immense professional satisfaction in being associated with an institution that continuously encourages innovation, research-oriented learning, and the holistic development of both students and teachers. The school creates an atmosphere where education goes beyond memorization and focuses on developing real understanding and lifelong learning skills.",
      "The name \"THE CREATOR\" beautifully reflects its vision of nurturing every child's potential. I strongly believe that students studying here receive an education that prepares them not only for examinations but also for future research, innovation, leadership, and meaningful contributions to society.",
    ],
  },
];

const CASE_STUDIES = [
  {
    key: "ikhbaat",
    name: "Ikhbaat",
    grade: "Cambridge A Level · 2025",
    poster: STUDENT_POSTERS.ikhbaat,
    result: "Noblitt Scholar (Rose-Hulman IT, USA) · AP Scholar Award · Double Major in Physics + CS with undergraduate research grants",
    story: "Distinguished from 4,250+ Early Action applicants at Rose-Hulman Institute of Technology to earn the Noblitt Scholars Program merit award, alongside the AP Scholar Award from College Board and topper status in both IGCSE and A Level. Selected an undergraduate research track in quantum computing.",
  },
  {
    key: "masira",
    name: "Masira",
    grade: "Cambridge A Level · 2025",
    poster: STUDENT_POSTERS.masira,
    result: "Full 100% scholarship — B.S. in Biotechnology, University of Nottingham (Ranked 7th in UK for Research)",
    story: "Placed on a fully-funded biotechnology track in the Biological Sciences faculty at Nottingham. Topper in both IGCSE and A Level; also qualified NEET in a first attempt with zero external coaching — a signal of concept-first preparation from the Concept Lab.",
  },
  {
    key: "kshitij",
    name: "Kshitij Agarwal",
    grade: "Cambridge A Level · 2025",
    poster: STUDENT_POSTERS.kshitij,
    result: "Full scholarship — BSc (Hons) Physics with Astrophysics, Lancaster University, UK",
    story: "Awarded a full scholarship after a Lancaster physics interview that specifically praised his \"excellent grasp of circular motion and Newtonian gravity\", his \"very strong skills in integral calculus\" and his \"clear passion for Physics and Astrophysics\".",
  },
  {
    key: "vihaan",
    name: "Vihaan Deshmukh",
    grade: "Grade 9 · NTP",
    poster: STUDENT_POSTERS.vihaan,
    result: "Global Gold Award · British Physics Olympiad (Junior Physics Challenge) · Bronze at PhysicsBowl 2026",
    story: "Competing across older cohorts, Vihaan brought home a global gold at the BPhO Junior Physics Challenge and a bronze at the international PhysicsBowl — all from a Grade-9 Concept Lab bench.",
  },
];

const ADMITS = [
  { region: "United Kingdom", schools: ["Imperial College London", "UCL", "King's College London", "University of York", "Nottingham", "Lancaster", "Reading", "Warwick", "Edinburgh"] },
  { region: "United States",  schools: ["Rose-Hulman IT", "Purdue", "Michigan State", "UT Dallas", "Rochester IT", "UMass"] },
  { region: "Europe & Asia",  schools: ["TU Delft", "KU Leuven", "NUS · Singapore", "HKUST"] },
];

export default function Outcomes() {
  return (
    <div data-testid="outcomes-page">
      <PageHero
        n="07"
        label="Proven Outcomes"
        lines={["Outcomes That Prove", "the Framework"]}
        intro="Consistent cohort performance and outstanding individual results from The Creator International School."
      />

      {/* 100% highlight + stats */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="border border-accent p-10 md:p-14" data-testid="outcomes-100-card">
              <div className="font-display text-[7rem] md:text-[10rem] leading-none text-accent">100<span className="text-5xl align-top">%</span></div>
              <p className="font-mono-label text-[11px] tracking-label uppercase text-white/60 mt-4">Board outcome — consistent cohort performance</p>
              <ul className="mt-6 space-y-3 border-t border-white/10 pt-6">
                <li className="flex items-start gap-3 text-white/80 leading-snug">
                  <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" />
                  <span><span className="text-accent font-semibold">100%</span> of participating students received international medals in <span className="font-semibold">BPhO</span></span>
                </li>
                <li className="flex items-start gap-3 text-white/80 leading-snug">
                  <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" />
                  <span><span className="text-accent font-semibold">100%</span> of NTP graduates got admission offers from top universities of <span className="font-semibold">UK &amp; USA</span></span>
                </li>
              </ul>
            </div>
          </Reveal>
          <div className="md:col-span-7 grid grid-cols-2 gap-x-10 gap-y-12 md:pl-10 md:border-l border-white/10">
            <div>
              <Stat value={3} label="× 100 in 2 years" />
              <p className="text-white/60 text-sm leading-relaxed mt-3 max-w-xs">
                3 students received <span className="text-white">100% scholarship / financial aid</span> in the last two years for undergraduate programmes in the UK and USA.
              </p>
            </div>
            <div>
              <Stat value={10} suffix=" yrs" label="Consecutive gold" />
              <p className="text-white/60 text-sm leading-relaxed mt-3 max-w-xs">
                <span className="text-white">1,500+ gold medals</span> and <span className="text-white">twice International Rank 1</span> in SOF Olympiads.
              </p>
            </div>
            <Stat value={1} label="Intl Olympiad Rank (twice)" />
            <Stat value={100} suffix="%" label="Scholarship pathways" />
          </div>
        </div>
      </section>

      {/* Chart */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="01">Cohort performance in prestigious examinations</SectionLabel>
          <div className="mt-8 max-w-4xl space-y-5" data-testid="outcomes-chart-description">
            <p className="text-white/75 text-lg leading-relaxed">
              These are not the scores of one or two exceptional students — they are <span className="text-white font-medium">cohort-wide results</span>, achieved batch after batch, across the world's most demanding examinations. When an entire class performs at this level in Cambridge A Levels, AMC, international Olympiads and board examinations simultaneously, it stops being luck and becomes <span className="text-accent">proof of a method</span>.
            </p>
            <p className="text-white/75 text-lg leading-relaxed">
              The clearest evidence came from the <span className="text-white font-medium">British Physics Olympiad (BPhO)</span> — where our <span className="text-white font-medium">entire Grade 11 class received international medals</span>, on their very first attempt. Every single student who sat the paper was awarded, prompting the BPhO organisers themselves to remark that our academic standards are "at their peak".
            </p>
            <p className="text-white/75 text-lg leading-relaxed">
              This is precisely what the PBL-TDLE philosophy promises: when concepts are built through demonstrations, experiments and systematic problem-solving — rather than memorised — <span className="text-white font-medium">every learner rises, not just the toppers</span>. The chart below is the cohort's collective answer to the question: does the framework work?
            </p>
          </div>
          <div className="mt-12 h-[360px] w-full" data-testid="outcomes-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={CHART} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1e293b" }} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {CHART.map((_, i) => (
                    <Cell key={i} fill="#1B4A7A" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Featured Recognitions — BPhO organizer quote + AP / AMC / PhysicsBowl / SOF */}
      <section id="recognitions" className="border-t border-white/10 bg-[#050B14] scroll-mt-24" data-testid="outcomes-recognitions">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="02">Featured International Recognitions</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            BPhO · AP · AMC — earned in the students' own words, and in the organisers'.
          </h2>

          {/* BPhO organizer quote — hero card */}
          <Reveal>
            <div
              className="mt-14 rounded-3xl overflow-hidden border border-white/10 grid lg:grid-cols-12 gap-0"
              style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }}
              data-testid="bpho-organizer-quote"
            >
              <div className="lg:col-span-7 p-8 md:p-14 text-onimg">
                <div className="inline-flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase text-onimg bg-white/15 rounded-full px-4 py-2">
                  <Sparkles className="w-4 h-4" /> BPhO Organizer's Remark
                </div>
                <Quote className="w-10 h-10 text-white/40 mt-8" />
                <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-onimg mt-4 leading-snug tracking-tight font-light">
                  {RECOGNITIONS.bphoQuote.quote}
                </blockquote>
                <p className="font-mono-label text-[11px] tracking-label uppercase text-onimg/80 mt-8">
                  — {RECOGNITIONS.bphoQuote.attribution}
                </p>
              </div>
              <div className="lg:col-span-5 min-h-[280px] lg:min-h-0 bg-white overflow-hidden border-t lg:border-t-0 lg:border-l border-white/10">
                <img
                  src={RECOGNITIONS.bphoQuote.src}
                  alt="BPhO organizer's remark for The Creator International School"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Reveal>

          {/* Recognition cards — BPhO, AP, AMC, PhysicsBowl, IO gold */}
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {RECOGNITIONS.cards.map((c, i) => (
              <Reveal key={c.key} delay={(i % 2) * 0.08} className="border border-white/10 rounded-2xl overflow-hidden bg-[#070d18]" data-testid={`recognition-${c.key}`}>
                <div className="grid grid-cols-12">
                  <div className="col-span-12 sm:col-span-5 bg-white overflow-hidden border-b sm:border-b-0 sm:border-r border-white/10 min-h-[220px]">
                    <img src={c.poster} alt={`${c.title} — recognition poster`} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="col-span-12 sm:col-span-7 p-6 md:p-7">
                    <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">{c.badge}</div>
                    <h3 className="font-display text-xl md:text-2xl text-white mt-3 tracking-tight leading-snug">{c.title}</h3>
                    <p className="text-white/75 leading-relaxed mt-3 text-sm">{c.headline}</p>
                    <ul className="mt-4 space-y-2">
                      {c.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-white/60 text-sm leading-relaxed">
                          <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Case-study student journeys */}
      <section id="journeys" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="outcomes-case-studies">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="03">Student journeys</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            The framework, told through the students who lived it.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08} className="border border-white/10 rounded-2xl overflow-hidden bg-[#050B14] flex flex-col" data-testid={`case-${c.key}`}>
                <div className="aspect-[3/4] overflow-hidden border-b border-white/10 bg-white">
                  <img src={c.poster} alt={`${c.name} · student journey`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">{c.grade}</div>
                  <h3 className="font-display text-2xl text-white mt-3 tracking-tight">{c.name}</h3>
                  <p className="text-white/70 leading-relaxed mt-3 text-sm flex-1">{c.story}</p>
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <div className="font-mono-label text-[10px] tracking-label uppercase text-white/45">Result</div>
                    <p className="text-white text-sm mt-2 leading-snug">{c.result}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* World university admissions */}
      <section id="admissions" className="border-t border-white/10 scroll-mt-24" data-testid="outcomes-admits">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="04">World university admissions</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Where our students go next.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-3xl">
            A representative — not exhaustive — list of universities where cohorts from The Creator International School have been admitted or offered places.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {ADMITS.map((a, i) => {
              const cols = ["text-c-blue", "text-c-amber", "text-c-teal"];
              return (
                <Reveal key={a.region} delay={i * 0.08} className="border border-white/10 rounded-xl p-8">
                  <div className="flex items-center gap-3">
                    <Globe2 className={`w-5 h-5 ${cols[i]}`} />
                    <span className={`font-mono-label text-[11px] tracking-label uppercase ${cols[i]}`}>{a.region}</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {a.schools.map((s) => (
                      <li key={s} className="flex items-start gap-3 text-white/80 leading-relaxed">
                        <GraduationCap className="w-4 h-4 text-white/40 shrink-0 mt-1" /> {s}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pull quote from a real result */}
      <section className="border-t border-white/10 bg-cream text-ink" data-testid="outcomes-pullquote">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="✦" dark>The Cohort Signal</SectionLabel>
          <Reveal>
            <blockquote className="font-display text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.1] tracking-tight mt-10 max-w-6xl font-light">
              “Ten straight years of gold. Two international Rank-1s. Three perfect 100s in two years. And every single result traceable back to the same PBL-TDLE cycle running daily inside the Concept Lab.”
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Watch — student outcome videos */}
      <section id="videos" className="border-t border-white/10 scroll-mt-24" data-testid="outcomes-videos">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <PlayCircle className="w-5 h-5 text-accent" />
            <SectionLabel n="05">Watch — Real Outcomes</SectionLabel>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Every student, a world-university offer.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-3xl">
            Three short films from The Creator International School — cohort results, in the school's own words.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {OUTCOME_VIDEOS.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08} className="border border-white/10 rounded-2xl overflow-hidden bg-[#050B14]">
                <div className="aspect-video bg-black">
                  <YouTubeEmbed id={v.id} title={v.title} />
                </div>
                <div className="p-5 md:p-6">
                  <div className="font-mono-label text-[10px] tracking-label uppercase text-accent">Video {i + 1}</div>
                  <h3 className="font-display text-lg md:text-xl text-white mt-2 tracking-tight leading-snug">{v.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* International Achievements · Awards & Honours — organised uniform grid */}
      <section id="achievements" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="achievements-gallery">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="06">International Achievements · Awards & Honours</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl">
            Real results, real recognition.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-3xl">
            Every certificate, poster and citation earned by our students — displayed as clearly as they deserve.
          </p>
          <div className="mt-8 max-w-4xl space-y-5" data-testid="achievements-description">
            <p className="text-white/75 text-lg leading-relaxed">
              Behind every poster in this gallery is a real student and a verifiable result. <span className="text-white font-medium">Ikhbaat</span> was named a <span className="text-white font-medium">Noblitt Scholar</span> at Rose-Hulman Institute of Technology, USA — distinguished from over 4,250 Early Action applicants — alongside the College Board's <span className="text-white font-medium">AP® Scholar Award</span>. <span className="text-white font-medium">Masira</span> earned a full 100% scholarship in Biotechnology at the University of Nottingham, and <span className="text-white font-medium">Kshitij</span> a full scholarship in Physics with Astrophysics at Lancaster University — <span className="text-accent">three perfect 100% scholarships in just two years</span>.
            </p>
            <p className="text-white/75 text-lg leading-relaxed">
              In competitions, the record is just as consistent: <span className="text-white font-medium">every participating student medalled at the British Physics Olympiad</span>; <span className="text-white font-medium">8 of 13 students</span> earned the Outstanding Achievement Award at the American Mathematics Competitions in our very first year; <span className="text-white font-medium">Vihaan (Grade 9)</span> took bronze at the international PhysicsBowl competing against older cohorts; and a decade of SOF International Olympiads has produced <span className="text-white font-medium">1,500+ gold medals and two International Rank 1 finishes</span> — spanning science, mathematics, English and general knowledge alike.
            </p>
            <p className="text-white/75 text-lg leading-relaxed">
              From Olympiad podiums to world-university scholarship letters, each recognition traces back to the same daily practice: concepts developed in the Concept Lab, sharpened through advanced problem-solving, and proven on international stages.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6 mt-14">
            {ACHIEVEMENTS.map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 0.05}>
                <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border border-white/10 bg-white hover:border-accent transition-colors">
                  <img
                    src={src}
                    alt={`Achievement ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050B14]/95 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3">
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-onimg">#{String(i + 1).padStart(2, "0")} · Recognition</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="font-mono-label text-[11px] tracking-label uppercase text-white/50 mt-12">
            ★ {ACHIEVEMENTS.length} certificates & posters shown · updated with every new cohort ★
          </p>
        </div>
      </section>

      {/* Testimonials — last section, mirrors last submenu item */}
      <section id="testimonials" className="border-t border-white/10 scroll-mt-24" data-testid="outcomes-testimonials">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="07">Testimonials — Voices from Our Community</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Students, parents and teachers — in their own words.
          </h2>

          {/* Featured long-form testimonials */}
          <div className="grid lg:grid-cols-3 gap-6 mt-14">
            {FEATURED_TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08} className="border border-white/10 rounded-2xl bg-[#070d18] p-8 md:p-9 flex flex-col" data-testid={`featured-testimonial-${i}`}>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 text-c-amber fill-c-amber" />
                  ))}
                </div>
                <div className="mt-5 space-y-4 flex-1">
                  {t.paras.map((p, j) => (
                    <p key={j} className="text-white/70 text-sm leading-relaxed">{p}</p>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-display text-xl text-white tracking-tight">{t.name}</div>
                  <div className="font-mono-label text-[10px] tracking-label uppercase text-white/50 mt-2">{t.role}</div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Video testimonials */}
          <div className="mt-16" data-testid="video-testimonials">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-logo-blue">Video Testimonials · Hear It First-Hand</div>
            <div className="grid sm:grid-cols-2 gap-8 mt-8">
              {[
                { id: "YB9brSBwW5A", t: "Testimonial — our community speaks" },
                { id: "DmNC93iO_tU", t: "Testimonial — parents on PBL-TDLE" },
                { id: "TSIPX1TV_UI", t: "Testimonial — students on learning by experiments" },
                { id: "5Desp5Fu-fk", t: "Testimonial — teachers on the framework" },
              ].map((v, i) => (
                <Reveal key={v.id} delay={(i % 2) * 0.08}>
                  <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid={`video-testimonial-${i}`}>
                    <YouTubeEmbed id={v.id} title={v.t} />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Educator voices — real classroom feedback */}
          <div className="mt-16" data-testid="educator-feedback">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-logo-blue">Educator Voices · Feedback From Our Classrooms</div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
              {EDUCATOR_FEEDBACK.map((t, i) => (
                <Reveal key={i} delay={(i % 3) * 0.08} className="border border-white/10 rounded-2xl overflow-hidden bg-[#070d18] flex flex-col" data-testid={`educator-feedback-${i}`}>
                  {t.img && (
                    <div className="border-b border-white/10 bg-white">
                      <img src={t.img} alt={`${t.r} — classroom activity`} loading="lazy" className="w-full h-auto block" />
                    </div>
                  )}
                  <div className="p-7 md:p-8 flex-1 flex flex-col">
                    <span className="font-display text-5xl text-accent leading-none">“</span>
                    <p className="text-white/75 text-sm leading-relaxed -mt-2 flex-1">{t.q}</p>
                    <div className="mt-6 pt-5 border-t border-white/10">
                      <div className="font-display text-lg text-white tracking-tight">{t.a}</div>
                      <div className="font-mono-label text-[10px] tracking-label uppercase text-white/50 mt-1.5">{t.r}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Short voices */}
          <div className="grid md:grid-cols-2 gap-px bg-white/10 mt-10 border border-white/10">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-[#050B14] p-8 md:p-10">
                <span className="font-display text-6xl text-accent leading-none">“</span>
                <p className="font-display text-xl md:text-2xl text-white/85 leading-snug -mt-4">{t.q}</p>
                <div className="mt-6 font-mono-label text-[11px] tracking-label uppercase text-white/50">
                  {t.a} · {t.r}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
